import { FC, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setMarketplaceCartQuantity } from '../../../redux/reducers/marketplaceCart';
import { BackButton, SortedTable } from '../../../components';
import ActionMenu from '../../../components/action-buttons/ActionButtons';
import Modal from '../../../components/modal/Modal';
import { MarketPlaceCartItem, MarketplaceCartItemRequest } from '../../../types/interfaces/marketPlace.interfaces';
import { specimenType } from '../../../types/enums/specimenType.enum';
import {
  BUTTON_CONSTANTS,
  LABEL_CONSTANTS,
  MARKETPLACE_CART_CONSTANTS,
  MARKETPLACE_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
} from '../../../constants/common';
import { toastMessages } from '../../../constants/errorMessages';
import { ROUTE_PATHS } from '../../../constants/routePaths';
import {
  addToMarketplaceCart,
  getMarketplaceCartItems,
  deleteMarketplaceCartItems,
  updateMarketplaceCartItem,
} from '../../../api/marketPlaceCartItemApi';
import { getMarketplaceSpecimenById } from '../../../api/marketPlaceSpecimenApi';
import { centsToDollars } from '../../../services/account.service';
import { showToast } from '../../../services/toast.service';
import {
  getGuestMarketplaceCartLines,
  syncGuestMarketplaceCartToAccount,
  removeGuestMarketplaceCartLine,
  updateGuestMarketplaceCartLineQuantity,
} from '../../../services/guestMarketplaceCart.service';
import { useGuestMarketplaceOptional } from '../../../context/guestMarketplaceContext';
import { Genders } from '../../../types/enums';
import './marketplaceCart.scss';

type MarketplaceCartProps = {
  accountId?: number;
  guestMode?: boolean;
};

const isMarketplaceCartItemSelectableForCheckout = (cartItem: MarketPlaceCartItem): boolean =>
  cartItem.marketPlaceSpecimen?.inStock !== false;

const isFemaleMarketplaceCartItem = (cartItem: MarketPlaceCartItem): boolean => {
  const gender = cartItem.marketPlaceSpecimen?.animal?.gender ?? cartItem.marketPlaceSpecimen?.specimen?.animal?.gender;
  return String(gender ?? '').toLowerCase() === Genders.Female.toLowerCase();
};

const getMarketplaceCartItemTotalCents = (cartItem: MarketPlaceCartItem): number => {
  const unitPriceCents = cartItem.marketPlaceSpecimen?.unitPriceInCents ?? 0;
  const qty = cartItem.cartQuantity ?? 0;
  return isFemaleMarketplaceCartItem(cartItem) ? unitPriceCents : unitPriceCents * qty;
};

const MarketplaceCart: FC<MarketplaceCartProps> = ({ accountId, guestMode = false }): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isLoggedIn = Boolean(useAppSelector(state => state.auth.appContext.token));
  const guestMarketplace = useGuestMarketplaceOptional();
  const [hasTriggeredGuestSync, setHasTriggeredGuestSync] = useState<boolean>(false);
  const [marketplaceCartItems, setMarketplaceCartItems] = useState<MarketPlaceCartItem[]>([]);
  const [marketplaceLoading, setMarketplaceLoading] = useState<boolean>(false);
  const [selectedMarketplaceIds, setSelectedMarketplaceIds] = useState<Set<number>>(new Set());

  const [isClearMarketplaceModalOpen, setIsClearMarketplaceModalOpen] = useState<boolean>(false);
  const [isEditMarketplaceModalOpen, setIsEditMarketplaceModalOpen] = useState<boolean>(false);
  const [editMarketplaceItem, setEditMarketplaceItem] = useState<MarketPlaceCartItem | null>(null);
  const [editMarketplaceQuantity, setEditMarketplaceQuantity] = useState<string>('1');

  const editMaxQuantity = Math.max(editMarketplaceItem?.marketPlaceSpecimen?.quantity ?? 1, 1);

  const handleGetGuestMarketplaceCartItems = useCallback(async () => {
    try {
      setMarketplaceLoading(true);
      const lines = getGuestMarketplaceCartLines();
      if (lines.length === 0) {
        setMarketplaceCartItems([]);
        guestMarketplace?.refreshGuestCartCount();
        return;
      }

      const include = 'Animal.Breed.Species, EmbryoStraw, Animal.Attachments, Specimen.SpecimenType, Accounts';
      const specimens = await Promise.all(
        lines.map(async line => {
          try {
            const { data } = await getMarketplaceSpecimenById(line.marketPlaceSpecimenId, { include });
            return data;
          } catch {
            return null;
          }
        }),
      );

      const items: MarketPlaceCartItem[] = lines.map((line, index) => ({
        marketPlaceCartItemId: line.marketPlaceSpecimenId,
        cartQuantity: Math.max(1, line.quantity),
        accountId: 0,
        account: specimens[index]?.account ?? null,
        marketPlaceSpecimenId: line.marketPlaceSpecimenId,
        marketPlaceSpecimen: specimens[index],
        sellerAccountId: line.sellerAccountId,
        sellerAccount: specimens[index]?.account ?? null,
      }));

      setMarketplaceCartItems(items);
      guestMarketplace?.refreshGuestCartCount();
    } finally {
      setMarketplaceLoading(false);
    }
  }, [guestMarketplace]);

  const handleGetMarketplaceCartItems = useCallback(async () => {
    if (guestMode) {
      await handleGetGuestMarketplaceCartItems();
      return;
    }

    if (accountId == null) return;
    try {
      setMarketplaceLoading(true);
      const { data } = await getMarketplaceCartItems({
        include: 'Accounts, MarketPlaceSpecimen.Specimen.Animal, MarketPlaceSpecimen.Specimen.SpecimenType',
        filter: `accountId eq ${accountId}`,
      });
      const items = data ?? [];
      setMarketplaceCartItems(items);
      const totalQty = items.reduce((sum, cartItem) => sum + (cartItem.cartQuantity ?? 0), 0);
      dispatch(setMarketplaceCartQuantity(totalQty));
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setMarketplaceLoading(false);
    }
  }, [accountId, dispatch, guestMode, handleGetGuestMarketplaceCartItems]);

  useEffect(() => {
    void handleGetMarketplaceCartItems();
  }, [handleGetMarketplaceCartItems]);

  useEffect(() => {
    setSelectedMarketplaceIds(prev => {
      const prevIds = Array.from(prev);
      const next = new Set<number>();
      for (const id of prevIds) {
        const item = marketplaceCartItems.find(cartItem => cartItem.marketPlaceCartItemId === id);
        if (item && isMarketplaceCartItemSelectableForCheckout(item)) {
          next.add(id);
        }
      }
      if (next.size === prev.size && prevIds.every(cartItemId => next.has(cartItemId))) {
        return prev;
      }
      return next;
    });
  }, [marketplaceCartItems]);

  useEffect(() => {
    const syncGuestCart = async () => {
      if (guestMode || accountId == null || hasTriggeredGuestSync) return;

      const lines = getGuestMarketplaceCartLines();
      if (lines.length === 0) return;

      setHasTriggeredGuestSync(true);
      setMarketplaceLoading(true);
      try {
        await syncGuestMarketplaceCartToAccount(accountId, addToMarketplaceCart);
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setMarketplaceLoading(false);
        await handleGetMarketplaceCartItems();
      }
    };

    void syncGuestCart();
  }, [accountId, guestMode, hasTriggeredGuestSync, handleGetMarketplaceCartItems]);

  if (accountId == null && !guestMode) {
    return (
      <div className="card marketplace-transactions-card">
        <h2>{MARKETPLACE_CART_CONSTANTS.MARKETPLACE_CART_SECTION_TITLE}</h2>
        <div className="desk-marketplace-cart">
          <SortedTable
            headers={[
              { displayName: TABLE_HEADER_CONSTANTS.SELECT },
              { displayName: TABLE_HEADER_CONSTANTS.ANIMAL_NAME },
              { displayName: TABLE_HEADER_CONSTANTS.ACCOUNT_OWNER },
              { displayName: TABLE_HEADER_CONSTANTS.INVENTORY_TYPE },
              { displayName: TABLE_HEADER_CONSTANTS.QUANTITY },
              { displayName: TABLE_HEADER_CONSTANTS.PRICE_UNIT },
              { displayName: MARKETPLACE_CONSTANTS.TOTAL_PRICE },
              { displayName: TABLE_HEADER_CONSTANTS.ACTION },
            ]}
            data={[]}
            height={256}
            loading={false}
            noResultsMessage={LABEL_CONSTANTS.NO_RESULTS_FOUND}
          />
        </div>
      </div>
    );
  }

  const toggleMarketplaceSelection = (cartItemId: number) => {
    const item = marketplaceCartItems.find(i => i.marketPlaceCartItemId === cartItemId);
    if (item && !isMarketplaceCartItemSelectableForCheckout(item)) {
      return;
    }
    setSelectedMarketplaceIds(prev => {
      const next = new Set(prev);
      if (next.has(cartItemId)) next.delete(cartItemId);
      else next.add(cartItemId);
      return next;
    });
  };

  const removeMarketplaceCartItems = async (ids: number[]) => {
    try {
      setMarketplaceLoading(true);
      if (guestMode) {
        ids.forEach(id => removeGuestMarketplaceCartLine(id));
      } else {
        await deleteMarketplaceCartItems(ids);
      }
      setSelectedMarketplaceIds(new Set());
      await handleGetMarketplaceCartItems();
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setMarketplaceLoading(false);
    }
  };

  const handleConfirmClearMarketplaceCart = async () => {
    try {
      setMarketplaceLoading(true);
      const allIds = marketplaceCartItems.map(cartItem => cartItem.marketPlaceCartItemId);
      if (allIds.length > 0) {
        if (guestMode) {
          allIds.forEach(id => removeGuestMarketplaceCartLine(id));
        } else {
          await deleteMarketplaceCartItems(allIds);
        }
      }
      setSelectedMarketplaceIds(new Set());
      await handleGetMarketplaceCartItems();
      setIsClearMarketplaceModalOpen(false);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setMarketplaceLoading(false);
    }
  };

  const openEditMarketplaceQuantity = (cartItem: MarketPlaceCartItem) => {
    const stockQuantity = Math.max(cartItem.marketPlaceSpecimen?.quantity ?? 1, 1);
    setEditMarketplaceItem(cartItem);
    setEditMarketplaceQuantity(String(Math.min(cartItem.cartQuantity ?? 1, stockQuantity)));
    setIsEditMarketplaceModalOpen(true);
  };

  const handleConfirmUpdateMarketplaceQuantity = async () => {
    if (!editMarketplaceItem) return;

    const qty = Number(editMarketplaceQuantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      showToast.error(LABEL_CONSTANTS.ENTER_VALID_QUANTITY);
      return;
    }

    const specimen = editMarketplaceItem.marketPlaceSpecimen;
    const specimenId = specimen?.specimenId;
    const sellerAccountId = editMarketplaceItem.sellerAccountId ?? specimen?.accountId;
    const marketPlaceSpecimenId = editMarketplaceItem.marketPlaceSpecimenId;
    const maxQty = Math.max(specimen?.quantity ?? 1, 1);
    const clampedQty = Math.min(Math.max(qty, 1), maxQty);

    if (!specimenId || !sellerAccountId || !marketPlaceSpecimenId) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      return;
    }

    const specimenTypeName = specimen?.specimen?.specimenType?.name ?? '';
    const isEmbryoType = specimenTypeName === specimenType.Embryo;

    const payload: MarketplaceCartItemRequest = {
      accountId: editMarketplaceItem.accountId,
      cartQuantity: clampedQty,
      sellerAccountId,
      specimenId,
      marketPlaceSpecimenId,
      ...(isEmbryoType ? { embryoStrawId: specimen?.embryoStrawId ?? 0 } : {}),
    };

    try {
      setMarketplaceLoading(true);
      if (guestMode) {
        updateGuestMarketplaceCartLineQuantity(editMarketplaceItem.marketPlaceSpecimenId, clampedQty);
      } else {
        await updateMarketplaceCartItem(editMarketplaceItem.marketPlaceCartItemId, payload);
      }
      setIsEditMarketplaceModalOpen(false);
      setEditMarketplaceItem(null);
      await handleGetMarketplaceCartItems();
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setMarketplaceLoading(false);
    }
  };

  const emptyCell = MARKETPLACE_CART_CONSTANTS.TABLE_CELL_EMPTY;

  const selectedMarketplaceCartItems = marketplaceCartItems.filter(cartItem =>
    selectedMarketplaceIds.has(cartItem.marketPlaceCartItemId),
  );

  const marketplaceTotalQty = selectedMarketplaceCartItems.reduce(
    (sum: number, cartItem: MarketPlaceCartItem) => sum + (cartItem.cartQuantity ?? 0),
    0,
  );
  const marketplaceTotalCents = selectedMarketplaceCartItems.reduce((sum: number, cartItem: MarketPlaceCartItem) => {
    return sum + getMarketplaceCartItemTotalCents(cartItem);
  }, 0);
  const marketplaceTotalDollarsValue = centsToDollars(marketplaceTotalCents);
  const marketplaceTotalDollars =
    typeof marketplaceTotalDollarsValue === 'number' ? marketplaceTotalDollarsValue.toFixed(2) : marketplaceTotalDollarsValue;

  const handleProceedToCheckout = () => {
    if (!accountId || selectedMarketplaceIds.size === 0) return;
    const selectedItems = marketplaceCartItems.filter(
      item => selectedMarketplaceIds.has(item.marketPlaceCartItemId) && isMarketplaceCartItemSelectableForCheckout(item),
    );
    if (selectedItems.length === 0) return;
    navigate(ROUTE_PATHS.APP_MARKETPLACE_CHECKOUT + accountId, {
      state: { selectedItems },
    });
  };

  return (
    <div className={`card marketplace-transactions-card${guestMode ? ' marketplace-transactions-card--guest' : ''}`}>
      <div className="marketplace-transactions">
        {!isLoggedIn && <BackButton onClick={() => navigate(-1)} />}
        <div className="marketplace-transactions__header">
          <h2>{MARKETPLACE_CART_CONSTANTS.MARKETPLACE_CART_SECTION_TITLE}</h2>
          {marketplaceCartItems.length > 0 && (
            <button
              type="button"
              className="button red medium inverted"
              onClick={() => setIsClearMarketplaceModalOpen(true)}
              disabled={marketplaceLoading}>
              {BUTTON_CONSTANTS.CLEAR_CART}
            </button>
          )}
        </div>
      </div>

      <div className="desk-marketplace-cart">
        <SortedTable
          headers={[
            { displayName: TABLE_HEADER_CONSTANTS.SELECT },
            { displayName: TABLE_HEADER_CONSTANTS.ANIMAL_NAME },
            { displayName: TABLE_HEADER_CONSTANTS.ACCOUNT_OWNER },
            { displayName: TABLE_HEADER_CONSTANTS.INVENTORY_TYPE },
            { displayName: TABLE_HEADER_CONSTANTS.QUANTITY },
            { displayName: TABLE_HEADER_CONSTANTS.PRICE_UNIT },
            { displayName: MARKETPLACE_CONSTANTS.TOTAL_PRICE },
            { displayName: TABLE_HEADER_CONSTANTS.ACTION },
          ]}
          data={marketplaceCartItems.map(cartItem => {
            const spec = cartItem.marketPlaceSpecimen;
            const selectableForCheckout = isMarketplaceCartItemSelectableForCheckout(cartItem);
            const animalName = spec?.animal?.name ?? spec?.specimen?.animal?.name ?? emptyCell;
            const accountOwnerName = spec?.account?.name ?? cartItem.sellerAccount?.name ?? emptyCell;
            const specimenTypeName = spec?.specimen?.specimenType?.name ?? '';
            const canEditQuantity = specimenTypeName === specimenType.Semen;
            const priceCents = spec?.unitPriceInCents ?? 0;
            const qty = cartItem.cartQuantity ?? 0;
            const totalCents = getMarketplaceCartItemTotalCents(cartItem);
            const priceVal = centsToDollars(priceCents);
            const totalVal = centsToDollars(totalCents);
            const priceStr = typeof priceVal === 'number' ? `$${priceVal.toFixed(2)}` : `$${priceVal}`;
            const totalStr = typeof totalVal === 'number' ? `$${totalVal.toFixed(2)}` : `$${totalVal}`;

            return [
              {
                content: (
                  <input
                    type="checkbox"
                    checked={selectedMarketplaceIds.has(cartItem.marketPlaceCartItemId)}
                    onChange={() => toggleMarketplaceSelection(cartItem.marketPlaceCartItemId)}
                    disabled={!selectableForCheckout}
                  />
                ),
                sortValue: animalName,
              },
              { content: animalName, sortValue: animalName },
              {
                content: accountOwnerName,
                sortValue: accountOwnerName === emptyCell ? '' : accountOwnerName,
              },
              {
                content: spec?.specimen?.specimenType?.name ?? spec?.specimen?.specimenTypeId ?? emptyCell,
                sortValue: spec?.specimen?.specimenType?.name ?? spec?.specimen?.specimenTypeId ?? '',
              },
              {
                content: (
                  <input
                    type="text"
                    className="quantity-input"
                    value={qty}
                    readOnly
                    aria-label={TABLE_HEADER_CONSTANTS.QUANTITY}
                  />
                ),
                sortValue: qty,
              },
              { content: priceStr, sortValue: priceCents },
              { content: totalStr, sortValue: totalCents },
              {
                content: (
                  <ActionMenu
                    actionButtons={[
                      ...(canEditQuantity
                        ? [
                            {
                              name: BUTTON_CONSTANTS.EDIT,
                              action: () => openEditMarketplaceQuantity(cartItem),
                            },
                          ]
                        : []),
                      {
                        name: BUTTON_CONSTANTS.REMOVE,
                        action: () => {
                          void removeMarketplaceCartItems([cartItem.marketPlaceCartItemId]);
                        },
                      },
                    ]}
                  />
                ),
              },
            ];
          })}
          height={256}
          loading={marketplaceLoading}
          noResultsMessage={LABEL_CONSTANTS.NO_TRANSACTIONS_FOUND}
        />
      </div>

      {marketplaceCartItems.length > 0 ? (
        <div className="xs-marketplace-cart">
          {marketplaceCartItems.map(cartItem => {
            const spec = cartItem.marketPlaceSpecimen;
            const animalName = spec?.animal?.name ?? spec?.specimen?.animal?.name ?? emptyCell;
            const sellerName = spec?.account?.name ?? cartItem.sellerAccount?.name ?? emptyCell;
            const qty = cartItem.cartQuantity ?? 0;
            const priceCents = spec?.unitPriceInCents ?? 0;
            const priceVal = centsToDollars(priceCents);
            const priceStr = typeof priceVal === 'number' ? `$${priceVal.toFixed(2)}` : `$${priceVal}`;
            const inventoryType = spec?.specimen?.specimenType?.name ?? spec?.specimen?.specimenTypeId ?? emptyCell;
            const canEditQuantity = (spec?.specimen?.specimenType?.name ?? '') === specimenType.Semen;
            const isSelected = selectedMarketplaceIds.has(cartItem.marketPlaceCartItemId);
            const selectableForCheckout = isMarketplaceCartItemSelectableForCheckout(cartItem);

            return (
              <div className="card max-width" key={cartItem.marketPlaceCartItemId}>
                <div className="marketplace-cart-card">
                  <div className="select-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleMarketplaceSelection(cartItem.marketPlaceCartItemId)}
                      disabled={!selectableForCheckout}
                    />
                  </div>
                  <div className="marketplace-cart-content">
                    <div className="marketplace-info">
                      <div className="marketplace-content">
                        <label>{`${TABLE_HEADER_CONSTANTS.ANIMAL_NAME}: `}</label>
                        <label>{animalName}</label>
                      </div>
                      <div className="marketplace-content">
                        <label>{`${TABLE_HEADER_CONSTANTS.ACCOUNT_OWNER}: `}</label>
                        <label>{sellerName}</label>
                      </div>
                      <div className="marketplace-content">
                        <label>{`${TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}: `}</label>
                        <label>{inventoryType}</label>
                      </div>
                    </div>
                    <div className="marketplace-info">
                      <div className="marketplace-content">
                        <label>{`${TABLE_HEADER_CONSTANTS.QUANTITY}: `}</label>
                        <label>{qty}</label>
                      </div>
                      <div className="marketplace-content">
                        <label>{`${TABLE_HEADER_CONSTANTS.PRICE_UNIT}: `}</label>
                        <label>{priceStr}</label>
                      </div>
                      <div className="marketplace-content">
                        <label>{`${TABLE_HEADER_CONSTANTS.SELLER}: `}</label>
                        <label>{sellerName}</label>
                      </div>
                    </div>
                  </div>
                  <div className="action-button">
                    <ActionMenu
                      actionButtons={[
                        ...(canEditQuantity
                          ? [
                              {
                                name: BUTTON_CONSTANTS.EDIT,
                                action: () => openEditMarketplaceQuantity(cartItem),
                              },
                            ]
                          : []),
                        {
                          name: BUTTON_CONSTANTS.REMOVE,
                          action: () => void removeMarketplaceCartItems([cartItem.marketPlaceCartItemId]),
                        },
                      ]}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="xs-marketplace-cart marketplace-transactions__no-results">{LABEL_CONSTANTS.NO_TRANSACTIONS_FOUND}</div>
      )}

      {marketplaceCartItems.length > 0 && (
        <>
          <div className="marketplace-transactions__summary">
            {selectedMarketplaceIds.size > 0 ? (
              <div className="marketplace-transactions__summary-row">
                <p>
                  {TABLE_HEADER_CONSTANTS.TOTAL_QTY}: {marketplaceTotalQty}
                </p>
                <p>
                  {MARKETPLACE_CONSTANTS.TOTAL_PRICE}: ${marketplaceTotalDollars}
                </p>
              </div>
            ) : null}
            <p className="marketplace-transactions__hint">{MARKETPLACE_CART_CONSTANTS.REVIEW_BEFORE_CHECKOUT_HINT}</p>
          </div>
          <div className="marketplace-transactions__actions">
            {guestMode ? (
              <Link className="button green" to={ROUTE_PATHS.SIGN_IN} state={{ fromGuestCart: true }}>
                {BUTTON_CONSTANTS.PROCEED_TO_CHECKOUT}
              </Link>
            ) : (
              <button
                type="button"
                className="button green"
                disabled={selectedMarketplaceIds.size === 0}
                onClick={handleProceedToCheckout}>
                {BUTTON_CONSTANTS.PROCEED_TO_CHECKOUT}
              </button>
            )}
          </div>
        </>
      )}

      <Modal isOpen={isClearMarketplaceModalOpen} onClose={() => setIsClearMarketplaceModalOpen(false)} width={490}>
        <div className="clear-marketplace-cart-modal">
          <div className="header">
            <h4>{MARKETPLACE_CART_CONSTANTS.CLEAR_CART_MODAL_TITLE}</h4>
          </div>
          <div className="body">
            <p>{MARKETPLACE_CART_CONSTANTS.CLEAR_CART_MODAL_BODY}</p>
          </div>
          <div className="footer">
            <button
              className="button blue inverted small"
              type="button"
              onClick={() => setIsClearMarketplaceModalOpen(false)}
              disabled={marketplaceLoading}>
              {BUTTON_CONSTANTS.CANCEL}
            </button>
            <button
              className="button red small"
              type="button"
              onClick={() => void handleConfirmClearMarketplaceCart()}
              disabled={marketplaceLoading}>
              {BUTTON_CONSTANTS.YES}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isEditMarketplaceModalOpen}
        onClose={() => {
          setIsEditMarketplaceModalOpen(false);
          setEditMarketplaceItem(null);
        }}
        width={490}>
        <div className="edit-marketplace-qty-modal">
          <div className="header">
            <h4>{MARKETPLACE_CART_CONSTANTS.EDIT_QUANTITY_MODAL_TITLE}</h4>
          </div>
          <div className="body">
            <div className="form-row">
              <label>{TABLE_HEADER_CONSTANTS.QUANTITY}</label>
              <input
                type="number"
                min={1}
                max={editMaxQuantity}
                value={editMarketplaceQuantity}
                onChange={event => {
                  if (event.target.value === '') {
                    setEditMarketplaceQuantity('');
                    return;
                  }
                  const parsed = Number(event.target.value);
                  if (!Number.isFinite(parsed)) return;
                  const nextQty = Math.min(Math.max(parsed, 1), editMaxQuantity);
                  setEditMarketplaceQuantity(String(nextQty));
                }}
              />
            </div>
          </div>
          <div className="footer">
            <button
              className="button blue inverted small"
              type="button"
              onClick={() => {
                setIsEditMarketplaceModalOpen(false);
                setEditMarketplaceItem(null);
              }}
              disabled={marketplaceLoading}>
              {BUTTON_CONSTANTS.CANCEL}
            </button>
            <button
              className="button green small"
              type="button"
              onClick={() => void handleConfirmUpdateMarketplaceQuantity()}
              disabled={marketplaceLoading}>
              {BUTTON_CONSTANTS.UPDATE}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MarketplaceCart;
